import { useState, useEffect } from "react";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { rpcCall, subscribe } from "../ws/client";
import { toast } from "./ui/Toast";
import { useQAProfile } from "../hooks/useQAProfile";
import "../styles/CoinFlip.scss";
import { useRef } from "react";


interface Game {
    id: string;
    type: string;
    status: string;
    creator_wallet: string;
    joiner_wallet: string | null;
    bet_amount: number;
    creator_choice: string;
    joiner_choice: string;
    result: string | null;
    result_value?: number | null;
    winner: string | null;
    payout: number | null;
    platform_fee: number | null;
    created_at: string;
    completed_at: string | null;
    refund_tx_hash?: string;
    refund_amount?: number;
    refund_at?: string;
    // dice5 fields
    players?: Array<{ wallet: string; roll?: number; joined_at?: string }>;
    max_players?: number;
    winners?: string[];
    winner_roll?: number;
}

type GameType = "coinflip" | "dice5";
type Choice = "red" | "black" | "dice5";

const GAME_META: Record<GameType, {
    title: string;
    subtitle: string;
    createTitle: string;
    choices: Choice[];
    labels: Record<Choice, string>;
    icons: Record<Choice, string>;
}> = {
    coinflip: {
        title: "P2P дуэль фишек",
        subtitle: "Красное или чёрное - шанс 50/50 удвоить TON",
        createTitle: "Создать дуэль фишек",
        choices: ["red", "black"],
        labels: { red: "КРАСНОЕ", black: "ЧЁРНОЕ", dice5: "Кости x5" },
        icons: { red: "🔴", black: "⚫", dice5: "🎲" }
    },
    dice5: {
        title: "P2P Кости x5",
        subtitle: "5 игроков бросают кости, победитель(и) получают банк!",
        createTitle: "Создать игру Кости x5",
        choices: ["dice5"],
        labels: { dice5: "Кости x5", red: "КРАСНОЕ", black: "ЧЁРНОЕ" },
        icons: { dice5: "🎲", red: "🔴", black: "⚫" }
    }
};


export default function CoinFlip() {
    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();
    const { isQAMode } = useQAProfile(wallet?.account?.address);
    const [activeGames, setActiveGames] = useState<Game[]>([]);
    const [myGames, setMyGames] = useState<Game[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [betAmount, setBetAmount] = useState("1");
    const [selectedGameType, setSelectedGameType] = useState<GameType | null>(null);
    const [selectedSide, setSelectedSide] = useState<Choice>("red");
    const [creating, setCreating] = useState(false);
    const [activeTab, setActiveTab] = useState<"available" | "my-games" | "stats">("available");
    const [flippingGameId, setFlippingGameId] = useState<string | null>(null);
    const [flipResult, setFlipResult] = useState<Choice | null>(null);
    const coinRef = useRef<HTMLDivElement>(null);

    const normalizeChoice = (choice: string | null | undefined, gameType: GameType): Choice => {
        if (choice === "red" || choice === "heads") return "red";
        if (choice === "black" || choice === "tails") return "black";
        if (choice === "dice5") return "dice5";
        return "black";
    };

    const getGameType = (game: Game): GameType => {
        if (game.type === "dice5") return "dice5";
        return "coinflip";
    };

    useEffect(() => {
        if (!selectedGameType) {
            return;
        }
        const firstChoice = GAME_META[selectedGameType].choices[0];
        setSelectedSide(firstChoice);
    }, [selectedGameType]);


    // Load data
    const loadData = async () => {
        if (!wallet?.account?.address || !selectedGameType) return;

        setLoading(true);
        try {
            // Load active games
            const gamesResponse = await rpcCall<{}, { games: Game[] }>(
                "games.list_active",
                { game_type: selectedGameType }
            );

            if (gamesResponse.result) {
                setActiveGames(gamesResponse.result.games || []);
            }

            // Load my games
            const myGamesResponse = await rpcCall<
                { wallet: string; game_type?: GameType },
                { games: Game[] }
            >("games.my_games", {
                wallet: wallet.account.address,
                game_type: selectedGameType
            });
            if (myGamesResponse.result) {
                setMyGames(myGamesResponse.result.games || []);
            }

            const statsResponse = await rpcCall<{}, { stats: any }>(
                "games.stats",
                { game_type: selectedGameType }
            );
            if (statsResponse.result) {
                setStats(statsResponse.result.stats);
            }
        } catch (error: any) {
            console.error("Failed to load games:", error);
            toast.error(error.message || "Не удалось загрузить игры");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        
        // Subscribe to game updates
        const unsubscribe = subscribe("games.updated", (params: any) => {
            console.log("🎮 Game update received:", params);
            loadData(); // Reload data when games are updated
        });
        
        // Cleanup subscription on unmount
        return () => {
            unsubscribe();
        };
    }, [wallet, selectedGameType]);


    // Create game
    const handleCreateGame = async () => {
        if (!wallet?.account?.address) {
            toast.error("Сначала подключите кошелёк");
            return;
        }

        if (!selectedGameType) {
            toast.error("Сначала выберите тип игры");
            return;
        }

        const amount = parseFloat(betAmount);
        if (isNaN(amount) || amount < 0.1) {
            toast.error("Минимальная ставка 0.1 TON");
            return;
        }

        if (amount > 100) {
            toast.error("Максимальная ставка 100 TON");
            return;
        }

        setCreating(true);
        try {
            // Step 1: Create game and get transaction
            const response = await rpcCall<
                {
                    creator_wallet: string;
                    bet_amount: number;
                    creator_choice: string;
                    game_type?: GameType;
                },
                { 
                    success: boolean;
                    game_id: string;
                    escrow_wallet: string;
                    transaction: any;
                    pending_tx_id: string;
                }
            >("games.create_coinflip", {
                creator_wallet: wallet.account.address,
                bet_amount: amount,
                creator_choice: selectedSide,
                game_type: selectedGameType
            });

            if (response.error || !response.result?.success) {
                toast.error(response.error?.message || "Не удалось создать игру");
                return;
            }

            const { game_id, transaction, pending_tx_id } = response.result;

            toast.info("Подпишите транзакцию для внесения депозита...");

            // Step 2: Send transaction through TonConnect
            const txResult = await tonConnectUI.sendTransaction(transaction);
            const txBoc = txResult.boc;

            toast.info("Транзакция отправлена, подтверждаем...");

            // Step 3: Confirm deposit
            const confirmResponse = await rpcCall<
                { game_id: string; tx_boc: string },
                { success: boolean; game: Game }
            >("games.confirm_creator_deposit", {
                game_id,
                tx_boc: txBoc
            });

            if (confirmResponse.error || !confirmResponse.result?.success) {
                toast.error("Не удалось подтвердить депозит");
                return;
            }

            toast.success("Игра создана! Ожидаем оппонента...");
            setBetAmount("1");
            loadData();

        } catch (error: any) {
            console.error("Create game error:", error);
            if (error.message?.includes("User rejects")) {
                toast.error("Транзакция отменена");
            } else {
                toast.error(error.message || "Не удалось создать игру");
            }
        } finally {
            setCreating(false);
        }
    };


    // Join game
    const handleJoinGame = async (gameId: string) => {
        if (!wallet?.account?.address) {
            toast.error("Сначала подключите кошелёк");
            return;
        }

        try {
            // Step 1: Join game and get transaction
            const response = await rpcCall<
                { game_id: string; joiner_wallet: string; is_qa_mode?: boolean },
                { 
                    success: boolean;
                    game_id: string;
                    escrow_wallet: string;
                    transaction: any;
                    pending_tx_id: string;
                }
            >("games.join_coinflip", {
                game_id: gameId,
                joiner_wallet: wallet.account.address,
                is_qa_mode: isQAMode
            });

            if (response.error || !response.result?.success) {
                toast.error(response.error?.message || "Не удалось присоединиться");
                return;
            }

            const { game_id, transaction } = response.result;

            toast.info("Подпишите транзакцию для внесения депозита...");

            // Step 2: Send transaction through TonConnect
            const txResult = await tonConnectUI.sendTransaction(transaction);
            const txBoc = txResult.boc;

            toast.info("Транзакция отправлена, игра запускается...");

            // Step 3: Confirm deposit and execute game
            const confirmResponse = await rpcCall<
                { game_id: string; tx_boc: string },
                { 
                    success: boolean;
                    game: Game;
                    result: string;
                    winner: string;
                    payout: number;
                }
            >("games.confirm_joiner_deposit", {
                game_id,
                tx_boc: txBoc
            });

            if (confirmResponse.error || !confirmResponse.result?.success) {
                toast.error("Не удалось подтвердить депозит");
                return;
            }

            const { game, result, winner, payout } = confirmResponse.result;
            const gameType = getGameType(game);
            setFlippingGameId(game.id);
            setFlipResult(normalizeChoice(result, gameType));

            setTimeout(() => {
                setFlippingGameId(null);
                setFlipResult(null);
                const isWinner = winner === wallet.account.address;
                if (isWinner) {
                    toast.success(`🎉 Вы выиграли ${payout} TON!`);
                } else {
                    toast.error("😔 Вы проиграли. Удачи в следующий раз!");
                }
                loadData();
            }, gameType === "coinflip" ? 2200 : 1600);

        } catch (error: any) {
            console.error("Join game error:", error);
            if (error.message?.includes("User rejects")) {
                toast.error("Транзакция отменена");
            } else {
                toast.error(error.message || "Не удалось присоединиться");
            }
        }
    };


    // Cancel game
    const handleCancelGame = async (gameId: string) => {
        if (!wallet?.account?.address) return;

        try {
            const response = await rpcCall<
                { game_id: string; wallet: string },
                { success: boolean; message: string; refund_tx_hash?: string; refund_amount?: number }
            >("games.cancel", {
                game_id: gameId,
                wallet: wallet.account.address
            });

            if (response.error) {
                toast.error(response.error.message || "Не удалось отменить игру");
            } else {
                if (response.result?.refund_amount) {
                    toast.success(`Игра отменена. Возврат: ${response.result.refund_amount.toFixed(4)} TON`);
                } else {
                    toast.success("Игра отменена");
                }
                loadData();
            }
        } catch (error: any) {
            toast.error(error.message || "Не удалось отменить игру");
        }
    };


    const renderGameCard = (game: Game, canJoin: boolean = false, canCancel: boolean = false) => {
        const isCreator = wallet?.account?.address === game.creator_wallet;
        const isJoiner = wallet?.account?.address === game.joiner_wallet;
        const isMyGame = isCreator || isJoiner || (game.players && game.players.some?.((p: any) => p.wallet === wallet?.account?.address));
        const gameType = getGameType(game);
        const labels = GAME_META[gameType].labels;
        const icons = GAME_META[gameType].icons;
        const isFlipping = flippingGameId === game.id && flipResult !== null;

        // DICE5: отображение игроков, бросков, победителей
        if (gameType === "dice5") {
            const players = game.players || [];
            const maxPlayers = game.max_players || 5;
            const winners = game.winners || [];
            const winnerRoll = game.winner_roll;
            const isCompleted = game.status === "completed";
            return (
                <div key={game.id} className={`game-card ${game.status} ${isFlipping ? "flipping" : ""}`}>
                    <div className="game-header">
                        <span className="game-status">{
                            game.status === "waiting" ? `⏳ Ожидание (${players.length}/${maxPlayers})` :
                            game.status === "in_progress" ? "🎲 Играем" :
                            game.status === "completed" ? "✅ Завершена" :
                            "❌ Отменена"
                        }</span>
                        <span className="game-bet">💰 {game.bet_amount} TON</span>
                    </div>
                    <div className="game-body">
                        <div className="dice5-players-list">
                            <table className="dice5-players-table">
                                <thead>
                                    <tr>
                                        <th>Игрок</th>
                                        <th>Бросок</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: maxPlayers }).map((_, idx) => {
                                        const p = players[idx];
                                        if (!p) {
                                            return (
                                                <tr className="dice5-player-row empty" key={idx}>
                                                    <td className="avatar empty">👤</td>
                                                    <td className="roll empty">⏳</td>
                                                    <td className="status waiting">⏳</td>
                                                </tr>
                                            );
                                        }
                                        const isYou = p.wallet === wallet?.account?.address;
                                        const isWinner = winners.includes?.(p.wallet);
                                        return (
                                            <tr
                                                className={`dice5-player-row${isYou ? " you" : ""}${isWinner && isCompleted ? " winner" : ""}`}
                                                key={p.wallet}
                                            >
                                                <td className={`avatar${isYou ? " you" : ""}${isWinner && isCompleted ? " winner" : ""}`}>
                                                    {isYou ? "🟦" : isWinner && isCompleted ? "🟩" : "🟨"}
                                                    <span className="wallet-name">{isYou ? <b>Вы</b> : `${p.wallet.slice(0, 6)}...`}</span>
                                                </td>
                                                <td className={`roll${isWinner && isCompleted ? " winner" : ""}`}>{p.roll ?? "-"}</td>
                                                <td className={`status${isWinner && isCompleted ? " winner" : isYou ? " you" : ""}`}>
                                                    {isWinner && isCompleted ? "🏆 Победитель" : isCompleted ? "-" : "✔️"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {(isFlipping || game.result) && (
                            <div className="game-result">
                                <div className={`result-dice ${isFlipping ? "rolling" : ""}`}>{game.result_value ?? "🎲"}</div>
                                <div className="result-text">
                                    {isCompleted ? (
                                        <>
                                            Лучший бросок: <b>{winnerRoll}</b><br />
                                            Победителей: <b>{winners.length}</b>
                                        </>
                                    ) : (
                                        <>Ожидание игроков...</>
                                    )}
                                </div>
                                {isMyGame && (game.result || isFlipping) && (
                                    <div className={`outcome ${winners.includes?.(wallet?.account?.address || "") && isCompleted ? "won" : "lost"} ${isFlipping ? "pending" : ""}`}>
                                        {isFlipping
                                            ? "Кубик катится..."
                                            : winners.includes?.(wallet?.account?.address || "")
                                                ? `🎉 Вы выиграли ${(game.payout || 0).toFixed(2)} TON!`
                                                : "😔 Вы проиграли"
                                        }
                                    </div>
                                )}
                            </div>
                        )}
                        {game.status === "cancelled" && game.refund_amount && isCreator && (
                            <div className="game-refund">
                                <div className="refund-title">💸 Возврат выполнен</div>
                                <div className="refund-amount">{game.refund_amount.toFixed(4)} TON</div>
                                <div className="refund-details">
                                    <small>Удержано: {(game.bet_amount * 0.05).toFixed(4)} TON (комиссия) + 0.05 TON (газ)</small>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="game-footer">
                        {canJoin && game.status === "waiting" && players.length < maxPlayers && !players.some((p: any) => p.wallet === wallet?.account?.address) && (
                            <button
                                className="btn btn-primary join-btn"
                                onClick={() => handleJoinGame(game.id)}
                                disabled={isFlipping}
                            >
                                🎮 Присоединиться
                            </button>
                        )}
                        {canCancel && game.status === "waiting" && isCreator && players.length === 1 && (
                            <button
                                className="btn btn-secondary cancel-btn"
                                onClick={() => handleCancelGame(game.id)}
                                disabled={isFlipping}
                            >
                                ❌ Отменить
                            </button>
                        )}
                        <span className="game-time">
                            {new Date(game.created_at).toLocaleTimeString()}
                        </span>
                    </div>
                </div>
            );
        }
        // ...existing code for coinflip/parity...
        const creatorChoice = normalizeChoice(game.creator_choice, gameType);
        const joinerChoice = normalizeChoice(game.joiner_choice, gameType);
        const resultChoice = game.result ? normalizeChoice(game.result, gameType) : null;
        const creatorEmoji = icons[creatorChoice];
        const joinerEmoji = icons[joinerChoice];
        return (
            <div key={game.id} className={`game-card ${game.status} ${isFlipping ? "flipping" : ""}`}>
                <div className="game-header">
                    <span className="game-status">{
                        game.status === "waiting" ? "⏳ Ожидание" :
                        game.status === "in_progress" ? "🎲 Играем" :
                        game.status === "completed" ? "✅ Завершена" :
                        "❌ Отменена"
                    }</span>
                    <span className="game-bet">💰 {game.bet_amount} TON</span>
                </div>
                <div className="game-body">
                    <div className="game-players">
                        <div className={`player ${isCreator ? "you" : ""}`}> 
                            <div className="player-side">{creatorEmoji}</div>
                            <div className="player-wallet">
                                {isCreator ? "👤 Вы" : `👤 ${game.creator_wallet.slice(0, 6)}...`}
                                {game.winner === game.creator_wallet && " 🏆"}
                            </div>
                        </div>
                        <div className="vs">VS</div>
                        <div className={`player ${isJoiner ? "you" : ""}`}> 
                            <div className="player-side">{joinerEmoji}</div>
                            <div className="player-wallet">
                                {game.joiner_wallet ? (
                                    isJoiner ? "👤 Вы" : `👤 ${game.joiner_wallet.slice(0, 6)}...`
                                ) : "?"}
                                {game.winner === game.joiner_wallet && " 🏆"}
                            </div>
                        </div>
                    </div>
                    {(isFlipping || game.result) && (
                        <div className="game-result">
                            {gameType === "coinflip" ? (
                                <div
                                    className={`result-coin ${isFlipping ? "animate" : ""} ${flipResult || resultChoice || "black"}`}
                                    ref={isFlipping ? coinRef : undefined}
                                >
                                    {((flipResult || resultChoice || "black") === "red") ? (
                                        <div className="coin-face red">🔴</div>
                                    ) : (
                                        <div className="coin-face black">⚫</div>
                                    )}
                                </div>
                            ) : (
                                <div className={`result-dice ${isFlipping ? "rolling" : ""}`}>
                                    {game.result_value ?? "🎲"}
                                </div>
                            )}
                            <div className="result-text">
                                Результат: {icons[(flipResult || resultChoice || "black") as Choice]} {labels[(flipResult || resultChoice || "black") as Choice]}
                            </div>
                            {isMyGame && (game.result || isFlipping) && (
                                <div className={`outcome ${game.winner === wallet?.account?.address && game.result ? "won" : "lost"} ${isFlipping ? "pending" : ""}`}>
                                    {isFlipping
                                        ? (gameType === "coinflip" ? "Фишка крутится..." : "Кубик катится...")
                                        : game.winner === wallet?.account?.address 
                                            ? `🎉 Вы выиграли ${game.payout} TON!`
                                            : "😔 Вы проиграли"
                                    }
                                </div>
                            )}
                        </div>
                    )}
                    {game.status === "cancelled" && game.refund_amount && isCreator && (
                        <div className="game-refund">
                            <div className="refund-title">💸 Возврат выполнен</div>
                            <div className="refund-amount">{game.refund_amount.toFixed(4)} TON</div>
                            <div className="refund-details">
                                <small>Удержано: {(game.bet_amount * 0.05).toFixed(4)} TON (комиссия) + 0.05 TON (газ)</small>
                            </div>
                        </div>
                    )}
                </div>
                <div className="game-footer">
                    {canJoin && game.status === "waiting" && !isCreator && (
                        <button
                            className="btn btn-primary join-btn"
                            onClick={() => handleJoinGame(game.id)}
                            disabled={isFlipping}
                        >
                            🎮 Присоединиться
                        </button>
                    )}
                    {canCancel && game.status === "waiting" && isCreator && (
                        <button
                            className="btn btn-secondary cancel-btn"
                            onClick={() => handleCancelGame(game.id)}
                            disabled={isFlipping}
                        >
                            ❌ Отменить
                        </button>
                    )}
                    <span className="game-time">
                        {new Date(game.created_at).toLocaleTimeString()}
                    </span>
                </div>
            </div>
        );
    };


    if (!wallet) {
        return (
            <div className="coinflip-container">
                <div className="connect-prompt">
                    <h2>P2P игры</h2>
                    <p>Подключите кошелёк для игры!</p>
                </div>
            </div>
        );
    }


    // Check if user has active game as creator
    const hasActiveGame = myGames.some(game => 
        game.creator_wallet === wallet?.account?.address && 
        (game.status === "waiting" || game.status === "pending_creator_deposit")
    );

    const selectedMeta = selectedGameType ? GAME_META[selectedGameType] : null;

    return (
        <div className="coinflip-container stylish-bg">
            <div className="coinflip-header">
                <h2>{selectedMeta ? selectedMeta.title : "P2P игры"}</h2>
                <p>{selectedMeta ? selectedMeta.subtitle : "Выберите режим и начните игру"}</p>
            </div>

            {!selectedGameType && (
                <div className="game-modes glassy">
                    <button
                        className={`game-mode-card mode-coinflip ${selectedGameType === "coinflip" ? "active" : ""}`}
                        onClick={() => setSelectedGameType("coinflip")}
                    >
                        <span className="mode-icon">🔴⚫</span>
                        <span className="mode-title">Дуэль фишек</span>
                        <span className="mode-subtitle">Классика 1v1 с удвоением банка</span>
                    </button>
                    <button
                        className={`game-mode-card mode-dice5 ${selectedGameType === "dice5" ? "active" : ""}`}
                        onClick={() => setSelectedGameType("dice5")}
                    >
                        <span className="mode-icon">🎲</span>
                        <span className="mode-title">Кости x5</span>
                        <span className="mode-subtitle">До 5 игроков, делёж банка по броску</span>
                    </button>
                    <a
                        href="https://t.me/tamamon_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="game-mode-card mode-tamamon"
                    >
                        <span className="mode-icon mode-image">
                            <img src="https://i.imgur.com/BS46Pns.png" alt="Tamamon" className="tamamon-logo" />
                        </span>
                        <span className="mode-title">Tamamon</span>
                        <span className="mode-subtitle">Telegram-бот с игровыми активностями</span>
                    </a>
                </div>
            )}

            {selectedGameType && (
                <div className="game-mode-reset">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setSelectedGameType(null)}
                    >
                        ← Назад к выбору игры
                    </button>
                </div>
            )}

            {/* Create Game Section */}
            {selectedGameType && !hasActiveGame ? (
                <div className="create-game-section glassy">
                    <h3>{selectedMeta?.createTitle}</h3>
                    <div className="create-form">
                        <div className="form-group">
                            <label>Ставка (TON): <span className="range">0.1 - 100 TON</span></label>
                            <input
                                type="number"
                                value={betAmount}
                                onChange={(e) => setBetAmount(e.target.value)}
                                min="0.1"
                                max="100"
                                step="0.1"
                                placeholder="1.0"
                                className="input-amount"
                            />
                        </div>

                        <div className="form-group">
                            <label>Ваш выбор:</label>
                            <div className="choice-buttons">
                                {selectedMeta?.choices.map((choice) => (
                                    <button
                                        key={choice}
                                        className={`choice-btn choice-${choice} ${selectedSide === choice ? "active" : ""}`}
                                        onClick={() => setSelectedSide(choice)}
                                        disabled={selectedGameType === "dice5"}
                                    >
                                        <span className="coin-icon">{selectedMeta?.icons[choice]}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            className="btn btn-primary btn-lg create-btn"
                            onClick={handleCreateGame}
                            disabled={creating}
                        >
                            {creating ? "Создание..." : "🎮 Создать игру"}
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Tabs */}
            {selectedGameType && (
            <div className="tabs glassy">
                <button
                    className={activeTab === "available" ? "active" : ""}
                    onClick={() => setActiveTab("available")}
                >
                    Доступные <span className="tab-count">{activeGames.length}</span>
                </button>
                <button
                    className={activeTab === "my-games" ? "active" : ""}
                    onClick={() => setActiveTab("my-games")}
                >
                    Мои игры <span className="tab-count">{myGames.length}</span>
                </button>
                <button
                    className={activeTab === "stats" ? "active" : ""}
                    onClick={() => setActiveTab("stats")}
                >
                    Статистика
                </button>
            </div>
            )}

            {/* Content */}
            {selectedGameType && (
            <div className="tab-content">
                {loading && (
                    <div className="loading">
                        <div className="spinner" />
                        <span>Загрузка игр...</span>
                    </div>
                )}

                {!loading && activeTab === "available" && (
                    <div className="games-list">
                        {activeGames.length === 0 ? (
                            <div className="empty-state">
                                <p>Нет активных игр. Создайте новую!</p>
                            </div>
                        ) : (
                            activeGames.map((game) => (
                                <div key={game.id} className="animated-fade-in">
                                    {renderGameCard(game, true, false)}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {!loading && activeTab === "my-games" && (
                    <div className="games-list">
                        {myGames.length === 0 ? (
                            <div className="empty-state">
                                <p>Вы еще не играли</p>
                            </div>
                        ) : (
                            myGames.map((game) => (
                                <div key={game.id} className="animated-fade-in">
                                    {renderGameCard(game, false, true)}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {!loading && activeTab === "stats" && stats && (
                    <div className="stats-section glassy">
                        <div className="stat-card">
                            <div className="stat-label">Всего игр</div>
                            <div className="stat-value">{stats.total_games}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Завершено</div>
                            <div className="stat-value">{stats.completed_games}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Активных</div>
                            <div className="stat-value">{stats.active_games}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Общий объём</div>
                            <div className="stat-value">{stats.total_volume.toFixed(2)} TON</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Комиссия платформы</div>
                            <div className="stat-value">{stats.platform_fees.toFixed(2)} TON</div>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
}
